import serviceAPI from 'api/service/serviceAPI';
import { Dispatch, AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { StateModel } from '../reducers';
import { fetchProfile, signOut } from './auth';
import { isAccessTokenAlive } from 'helpers/commonHelpers';
import { ClientGroupStatuses } from 'enums/onboarding/crm';
import { getGeolocationStatus } from 'helpers/sessionHelpers';
import { USER_TAG_IS_LOGGED_IN_FIRST_TIME } from 'constants/userTags';
import { ACTIVE_ANNOUNCEMENT_MESSAGE_VERSION } from 'constants/announcementModal';
import { AppTrace } from 'services/azure-app-insights/appTrace';
import { clearLocalStorage, getAccessToken } from 'helpers/localStorageHelpers';
import {
  clearUserSession,
  consumeShowAnnouncementAfterLogin,
  getUserSession,
} from 'helpers/sessionStorageHelpers';

// Actions
export enum ActionType {
  SET_BADGES = 'app/SET_BADGES',
  SET_INITIALIZED_STATUS = 'app/SET_INITIALIZED_STATUS',
  SET_ACTIVITY_TIMESTAMP = 'app/SET_ACTIVITY_TIMESTAMP',
  SET_APP_INSIGHTS_CONNECTION_STRINGS = 'app/SET_APP_INSIGHTS_CONNECTION_STRINGS',
  SET_WELCOME_MODAL_VISIBILITY_STATUS = 'app/SET_WELCOME_MODAL_VISIBILITY_STATUS',
  SET_ANNOUNCEMENT_VISIBILITY_STATUS = 'app/SET_ANNOUNCEMENT_VISIBILITY_STATUS',
  CLEAN_UP = 'app/CLEAN_UP',
}

// Action creators
export const setInitializedStatus = (newStatus: boolean) => ({
  type: ActionType.SET_INITIALIZED_STATUS,
  payload: { status: newStatus },
});

export const setLastActivityTimestamp = (lastActivityTimestamp: number) => ({
  type: ActionType.SET_ACTIVITY_TIMESTAMP,
  payload: { lastActivityTimestamp },
});

export const cleanUpAppReducer = () => ({
  type: ActionType.CLEAN_UP,
  payload: null,
});

export const setWelcomeModalVisibilityStatus = (status: boolean) => ({
  type: ActionType.SET_WELCOME_MODAL_VISIBILITY_STATUS,
  payload: { status },
});

export const setAnnouncementVisibilityStatus = (
  status: boolean,
  messageVersion: string | null = null,
) => ({
  type: ActionType.SET_ANNOUNCEMENT_VISIBILITY_STATUS,
  payload: {
    status,
    messageVersion: status ? messageVersion : null,
  },
});

export const initializeApp = () => async (dispatch: any) => {
  dispatch(setInitializedStatus(false));

  const geolocationStatus = await getGeolocationStatus();
  if (geolocationStatus === 'denied') {
    clearUserSession();
  }

  if (geolocationStatus === 'other-no-access') {
    const currentSession = getUserSession();
    if (currentSession && currentSession.geolocation) {
      clearUserSession();
    }
  }

  const accessToken = getAccessToken();
  if (!accessToken || (accessToken && !isAccessTokenAlive(accessToken))) {
    dispatch(setInitializedStatus(true));
    clearLocalStorage();
  } else {
    dispatch(initializeAppForAuthorizedUser());
  }
};

// TODO:
// 1) Update the logic for checking if the client group is approved (need to check from the user permissions)
export const initializeWelcomeModalStatus =
  () => (dispatch: Dispatch, getState: () => StateModel) => {
    const { auth, applications } = getState();

    const isUserLoggedInFirstTime = auth.profileData?.tags.includes(
      USER_TAG_IS_LOGGED_IN_FIRST_TIME,
    );
    const isActiveApplicationApproved =
      applications.activeApplication?.clientGroup?.status ===
      ClientGroupStatuses.Approved;

    if (isUserLoggedInFirstTime && isActiveApplicationApproved) {
      dispatch(setWelcomeModalVisibilityStatus(true));
    }
  };

export const initializeAnnouncementStatus =
  () => (dispatch: Dispatch, getState: () => StateModel) => {
    const { auth, applications } = getState();
    const activeAnnouncementMessageVersion =
      ACTIVE_ANNOUNCEMENT_MESSAGE_VERSION;
    const isFreshLoginFlagConsumedTrue = consumeShowAnnouncementAfterLogin();
    if (activeAnnouncementMessageVersion === null) {
      dispatch(setAnnouncementVisibilityStatus(false, null));
      return false;
    }

    const isAuthorizedPostLoginContext = auth.isAuthorized;
    const isApprovedClientGroupSelected =
      applications.activeApplication?.clientGroup?.status ===
      ClientGroupStatuses.Approved;

    // Locked eligibility contract for this rollout.
    const shouldShowAnnouncement =
      isFreshLoginFlagConsumedTrue &&
      isAuthorizedPostLoginContext &&
      isApprovedClientGroupSelected;

    if (shouldShowAnnouncement) {
      AppTrace.announcementShown(activeAnnouncementMessageVersion);
      dispatch(
        setAnnouncementVisibilityStatus(true, activeAnnouncementMessageVersion),
      );
      return true;
    }

    dispatch(setAnnouncementVisibilityStatus(false, null));
    return false;
  };

export const dismissAnnouncementAndInitializeWelcome =
  () => (dispatch: ThunkDispatch<StateModel, unknown, AnyAction>) => {
    dispatch(setAnnouncementVisibilityStatus(false, null));
    dispatch(initializeWelcomeModalStatus());
  };

export const initializeAppForAuthorizedUser = () => async (dispatch: any) => {
  try {
    await dispatch(initializeAppInsights());
    await dispatch(fetchProfile());
    dispatch(setInitializedStatus(true));
    const isAnnouncementVisible = dispatch(initializeAnnouncementStatus());

    if (!isAnnouncementVisible) {
      dispatch(initializeWelcomeModalStatus());
    }
  } catch {
    dispatch(signOut());
  }
};

const initializeAppInsights = () => async (dispatch: Dispatch) => {
  const response = await serviceAPI.fetchFrontendConfig();

  dispatch({
    type: ActionType.SET_APP_INSIGHTS_CONNECTION_STRINGS,
    payload: { connectionString: response.insights.connectionString },
    key: 'connectionString',
  });
};

export const getBadges = () => async (dispatch: Dispatch) => {
  const response = await serviceAPI.fetchBadges();

  dispatch({
    type: ActionType.SET_BADGES,
    payload: {
      badges: {
        pendingTransactionsCount: response.pendingApprovalTransactions,
        pendingClientRequestsCount: response.clientRequests,
        pendingAccountManagementWorkflowsCount:
          response.pendingAccountManagementWorkflows,
      },
    },
    key: 'badges',
  });
};
